import { createContext, useContext, useState } from "react";

import { LogSentryException } from "@shapr3d/frontend-lib";
import { useMutation } from "@tanstack/react-query";

import { queryKeys } from "../config";
import PaddleAdapter from "../lib/PaddleAdapter";
import { UMSrequestClient } from "../stores/requestClient";
import type { Subscription } from "./useAccountTypes";
import type { IndividualSubscription } from "./useTeamsTypes";

export const convertIndividualSubscriptionToLegacySubscription = (
  subscription: IndividualSubscription
): Subscription => ({
  id: subscription.id,
  teamSubscriptionId: undefined,
  type: subscription.type,
  expiration: new Date(subscription.expirationDate),
  autorenewal: subscription.autoRenewal,
  isTrial: subscription.isTrial,
  subscriptionPeriod: subscription.subscriptionPeriod,
  paddleId: subscription.paddleId,
  tier: subscription.tier,
  gracePeriodExpirationDate: subscription.gracePeriodExpirationDate
    ? new Date(subscription.gracePeriodExpirationDate)
    : undefined,
  isAutoRenewEditable: subscription.isAutoRenewEditable,
  isOnlyCancelable: subscription.isOnlyCancelable,
  teamId: undefined,
  teamName: undefined,
});

const PaddleContext = createContext<{
  subscriptionIdToPollForGracePeriodUpdate: number | undefined;
  setSubscriptionIdToPollForGracePeriodUpdate: React.Dispatch<
    React.SetStateAction<number | undefined>
  >;
}>({
  subscriptionIdToPollForGracePeriodUpdate: undefined,
  setSubscriptionIdToPollForGracePeriodUpdate: () => undefined,
});

export const usePaddleContext = () => {
  return useContext(PaddleContext);
};

export const PaddleProvider = ({ children }: { children: React.ReactNode }) => {
  const [subscriptionIdToPollForGracePeriodUpdate, setSubscriptionIdToPollForGracePeriodUpdate] =
    useState<number | undefined>(undefined);

  return (
    <PaddleContext.Provider
      value={{
        subscriptionIdToPollForGracePeriodUpdate,
        setSubscriptionIdToPollForGracePeriodUpdate,
      }}
    >
      {children}
    </PaddleContext.Provider>
  );
};

export const useUpdateApplePaymentInfo = () => {
  return useMutation({
    mutationKey: ["update-apple-payment-info"],
    mutationFn: () =>
      new Promise((resolve, reject) => {
        try {
          window.open("https://appleid.apple.com/account/manage", "_blank", "noopener,noreferrer");
          resolve(true);
        } catch (err) {
          reject(err);
        }
      }),
  });
};

type PaymentInfoUpdateResponseData = {
  updateUrl: string;
};

export const useUpdatePaddlePaymentInfo = () => {
  const { setSubscriptionIdToPollForGracePeriodUpdate } = usePaddleContext();

  return useMutation({
    mutationKey: [...queryKeys.authenticatedQueryKey, "update-paddle-payment-info"],
    mutationFn: async (subscription?: Subscription) => {
      if (!subscription || !subscription.paddleId) {
        throw new Error("Paddle ID is missing");
      }

      const { updateUrl } = await UMSrequestClient.authenticatedRequest({
        method: "GET",
        url: `/subscriptions/${subscription.paddleId}/update-url`,
      })
        .then((response) => response.json<PaymentInfoUpdateResponseData>())
        .catch((err) => {
          LogSentryException({
            messagePrefix: "Paddle - usePaddleUpdateURL",
            error: err,
          });

          throw err;
        });

      if (!updateUrl) {
        throw new Error("Paddle update URL data is missing");
      }

      const completed = await PaddleAdapter.getInstance().openUpdatePayment(updateUrl);
      if (completed && subscription.gracePeriodExpirationDate) {
        setSubscriptionIdToPollForGracePeriodUpdate(subscription.id);
      }

      return { completed };
    },
  });
};
